import React, { useCallback, useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { normalizeChatbotMessageId, postChatbotFeedback } from '@/lib/chatbotAsk';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

/**
 * Thumbs up/down for a chatbot assistant answer. Hidden when message_id is null.
 */
export default function ChatBotAnswerFeedback({
  message,
  tenantId,
  userId,
  sessionId,
  headers = {},
  disabled = false,
}) {
  const messageId = normalizeChatbotMessageId(message?.messageId ?? message?.message_id);
  const [rating, setRating] = useState(null); // 'up' | 'down'
  const [status, setStatus] = useState('idle'); // idle | thanks | reason | later | error
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sendFeedback = useCallback(
    async ({ nextRating, nextReason }) => {
      if (!messageId || !tenantId || !userId || submitting) return { ok: false };
      setSubmitting(true);
      try {
        const result = await postChatbotFeedback({
          apiBaseUrl: API_BASE_URL,
          messageId,
          rating: nextRating,
          reason: nextReason,
          tenantId,
          userId,
          sessionId,
          headers,
        });
        if (result.ok) return result;
        if (result.unavailable) {
          setStatus('later');
        } else {
          setStatus('error');
        }
        return result;
      } catch (err) {
        console.error('Chatbot feedback failed:', err);
        setStatus('error');
        return { ok: false };
      } finally {
        setSubmitting(false);
      }
    },
    [headers, messageId, sessionId, submitting, tenantId, userId],
  );

  const onThumbUp = useCallback(async () => {
    if (disabled || submitting || !messageId || !sessionId) return;
    setRating('up');
    setStatus('thanks');
    setReason('');
    await sendFeedback({ nextRating: 'up' });
  }, [disabled, messageId, sendFeedback, sessionId, submitting]);

  const onThumbDown = useCallback(async () => {
    if (disabled || submitting || !messageId || !sessionId) return;
    setRating('down');
    setStatus('reason');
    await sendFeedback({ nextRating: 'down' });
  }, [disabled, messageId, sendFeedback, sessionId, submitting]);

  const onSubmitReason = useCallback(async () => {
    if (disabled || submitting || !messageId || !sessionId || rating !== 'down') return;
    const trimmed = String(reason || '').trim();
    if (!trimmed) {
      setStatus('thanks');
      return;
    }
    const result = await sendFeedback({ nextRating: 'down', nextReason: trimmed });
    if (result?.ok) setStatus('thanks');
  }, [disabled, messageId, rating, reason, sendFeedback, sessionId, submitting]);

  if (!messageId || message?.isStreaming) return null;

  const thumbClass = (active) =>
    `h-7 w-7 p-0 rounded-md ${
      active
        ? 'bg-[#00C2C2]/15 text-[#00A3A3]'
        : 'text-[#5C6B7A] hover:bg-gray-100 hover:text-[#0A1A2F]'
    }`;

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={thumbClass(rating === 'up')}
          aria-label="Helpful"
          aria-pressed={rating === 'up'}
          disabled={disabled || submitting || !sessionId}
          onClick={onThumbUp}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={thumbClass(rating === 'down')}
          aria-label="Not helpful"
          aria-pressed={rating === 'down'}
          disabled={disabled || submitting || !sessionId}
          onClick={onThumbDown}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </Button>
        {status === 'thanks' ? (
          <span className="ml-1 text-[11px] text-[#5C6B7A]">Thanks for the feedback</span>
        ) : null}
        {status === 'later' ? (
          <span className="ml-1 text-[11px] text-[#5C6B7A]">Try again later</span>
        ) : null}
        {status === 'error' ? (
          <span className="ml-1 text-[11px] text-[#5C6B7A]">Couldn&apos;t save — try again later</span>
        ) : null}
      </div>

      {status === 'reason' && rating === 'down' ? (
        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitReason();
          }}
        >
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What was wrong? (optional)"
            className="h-8 text-xs"
            disabled={disabled || submitting || !sessionId}
            maxLength={500}
          />
          <Button
            type="submit"
            variant="outline"
            size="sm"
            className="h-8 text-xs shrink-0"
            disabled={disabled || submitting || !sessionId}
          >
            Send
          </Button>
        </form>
      ) : null}
    </div>
  );
}
